/* eslint-disable jsdoc/no-undefined-types */
import cartData from '#shared/util/cartData.js';
import invalidCartRedirectMiddleware from '#shared/middleware/invalidCartRedirectMiddleware.js';
import getCartMiddleware from '#shared/middleware/getCartMiddleware.js';

import cButton from '#themes/default/default/components/button/button.js';
import cButtons from '#themes/default/default/components/buttons/buttons.js';
import cContentLayout from '#themes/default/default/layouts/contentLayout/contentLayout.js';
import cFormInput from '#themes/default/default/components/formInput/formInput.js';
import cHeader from '#themes/default/default/components/header/header.js';
import cSkeleton from '#themes/default/default/components/skeleton/skeleton.js';

import isValid from '#frontend/util/isValid.js';

import template from './companyDetails.html' with { type: 'html' };

/**
 * @memberof CompanyDetailsPage
 * @feature Tax exemption for EU countries (VAT) [GEN]
 */
export default {
	...cartData,
	middleware: [
		invalidCartRedirectMiddleware,
		getCartMiddleware
	],
	components: {
		cButton,
		cButtons,
		cContentLayout,
		cFormInput,
		cHeader,
		cSkeleton
	},
	meta() {
		return {
			title: `${this.t('global_metaTitle')} - ${this.t('companyDetails_metaTitle')}`
		};
	},
	data() {
		return {
			formValid: false
		};
	},
	computed: {
		taxExample() {
			const country = this.order.countryList.find((country) => country.value === this.customer.billing.country);
			const countryTaxPlaceholder = country ? country.taxPlaceholder : '';
			return this.form.taxIdentifier && this.form.taxIdentifier.placeholder || countryTaxPlaceholder;
		},
		isAddressDisabled() {
			// Disable address field if it's visible on the first step
			return Boolean(this.order.displayAddressField || this.form.initial?.address);
		}
	},
	mounted() {
		this.tracking.checkout({
			url: this.url,
			page: this.page,
			order: this.order,
			theme: this.theme
		});
		this.tracking.companyInfo({
			app: this.app,
			url: this.url,
			page: this.page,
			selectedPayment: this.selectedPayment,
			order: this.order,
			theme: this.theme
		});
		this.utils.hideLoader();
	},
	methods: {
		async handleCheckoutFormSubmit() {
			// Present loading state to the user
			this.utils.showLoader();
			// Submit the order and return the result
			try {
				if (await this.startSubmitFlow()) {
					await this.$router.push(this.getLink('confirm-order')).catch(() => {});
				}
			} catch {
				this.utils.hideLoader();
				this.utils.setPaymentFailed();
				await this.$nextTick();
				await this.$router.push(this.getLink('shopping-cart')).catch(() => {});
			}
		},
		validateForm() {
			this.formValid = isValid('form');
		},
		async startSubmitFlow() {
			// Validate customer fields
			this.validateForm();
			if (!this.formValid) {
				this.utils.hideLoader();
				return false;
			}
			// Remember customer selection upon page reload
			this.rememberForm('customer', 'SET_CUSTOMER', this.customer);
			this.rememberForm('form', 'SET_FORM', this.form);
			// Apply customer company details
			await this.utils.applyCompany({
				companyName: this.customer.billing.companyName,
				taxId: this.customer.billing.taxId,
				line1: this.customer.billing.line1,
				line2: this.customer.billing.line2
			});
			// Remember a new customerType and relevant data of tax registrations
			this.rememberForm('form', 'SET_FORM', this.form);
			// Get new payment source for updated details
			const paymentSource = await this.paymentContainer.getPaymentSource();
			// Apply the payment method source updated with current customer details
			await this.utils.applyPayment(paymentSource);
			// Show the offer modal window
			if (this.shouldGetOffer) {
				this.utils.hideLoader();
				this.utils.showOfferModal();
				return false;
			}
			// Continue to the next checkout step
			return true;
		}
	},
	template
};
